'use client';

/**
 * Phase 4 sync panel. Handles the three user-facing flows:
 *
 *   1. Set up sync (first device): generate a 12-word BIP-39 recovery
 *      phrase, show it, enroll after the user confirms.
 *   2. Restore from recovery phrase (fresh device, user has the phrase):
 *      paste 12 words, derive the same key, enroll.
 *   3. Pair with existing device via QR (fresh device, phrase not at hand):
 *      generate ephemeral NaCl keypair + pair room, show QR, wait for a
 *      sealed-box ciphertext, decrypt, enroll.
 *
 * On an already-enrolled device, the panel also exposes:
 *   - "Show recovery phrase" (re-display for backup)
 *   - "Add another device" (camera scan → seal key → send over pair room)
 *   - Disconnect (clears phrase + key + connection)
 */

import { useEffect, useRef, useState } from 'react';
import { Wifi, WifiOff, QrCode, ScanLine, KeyRound, Copy, Check, X, ShieldAlert, AlertTriangle, Info } from 'lucide-react';
import QRCode from 'qrcode';
import jsQR from 'jsqr';
import { syncClient } from '@/lib/syncClient';
import {
  createPairSession,
  encodePairQR,
  decodePairQR,
  sealAccountKey,
  openSealedAccountKey,
  bytesToBase64,
  base64ToBytes,
  type PairSession,
} from '@/lib/pairing';
import { loadKey } from '@/lib/accountKey';
import { reportSyncedOnce } from '@/lib/metrics';
import { log, readRecentErrors, type LogEntry } from '@/lib/log';
import { useI18n } from '@/lib/i18n';
import { ZineButton } from '@/components/ui/ZineButton';
import { Chip } from '@/components/ui/Chip';
import PhraseGrid from '@/components/PhraseGrid';

type SyncStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
type Mode =
  | 'idle'
  | 'show-new-phrase'
  | 'restore-phrase'
  | 'show-phrase'
  | 'show-qr'
  | 'scan-qr'
  | 'show-errors';

// Mirrors src/lib/syncClient.ts — overridable via NEXT_PUBLIC_RELAY_WS_URL
// so the containerized test runner can point the pairing WS at its
// in-cluster relay instead of the prod Cloudflare Worker. Production
// deploys never set this env var, so the default kicks in.
const RELAY_URL =
  process.env.NEXT_PUBLIC_RELAY_WS_URL ??
  'wss://knitlib-sync.z3nlabs.workers.dev/sync';

export default function SyncPanel() {
  const { t } = useI18n();
  const [status, setStatus] = useState<SyncStatus>('disconnected');
  const [enrolled, setEnrolled] = useState(false);
  const [mode, setMode] = useState<Mode>('idle');

  // === React-side diagnostic hook for E2E (post-nav timing investigation) ===
  function pushReactDiag(type: string, detail?: { handler?: string; to?: string; [k: string]: unknown }) {
    const hook = (window as any).__knitlib_test;
    if (!hook) return;

    if (!hook.reactDiag) {
      hook.reactDiag = {
        events: [],
        handlersFired: {
          startRestore: 0,
          confirmRestore: 0,
          cancelMode: 0,
          startNewSetup: 0,
          startShowQR: 0,
        },
        lastMode: 'idle',
      };
    }

    const diag = hook.reactDiag;
    diag.events.push({ ts: Date.now(), type, detail });

    if (detail?.handler) {
      diag.handlersFired[detail.handler] =
        (diag.handlersFired[detail.handler] || 0) + 1;
    }
    if (type === 'mode-change') {
      diag.lastMode = detail?.to;
    }
  }

  // Diagnostic: log every mode change + push to test hook
  useEffect(() => {
    console.log('[ReactDiag] mode changed →', mode);
    pushReactDiag('mode-change', { to: mode });
  }, [mode]);
  const [error, setError] = useState<string | null>(null);

  // Set-up-new-phrase flow
  const [newPhrase, setNewPhrase] = useState<string>('');

  // Restore flow
  const [restoreInput, setRestoreInput] = useState('');

  // Show-phrase (already enrolled) flow
  const [revealedPhrase, setRevealedPhrase] = useState<string | null>(null);

  // Show-QR flow (new device waiting for sealed key)
  const pairSessionRef = useRef<PairSession | null>(null);
  const pairWsRef = useRef<WebSocket | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [pairMessage, setPairMessage] = useState<string>('');
  const [pairSuccess, setPairSuccess] = useState(false);

  // Phase 26.2: surface accumulating sync errors in the UI. Polls the
  // debug log's error-level entries every few seconds and shows a red
  // badge with a count. The user can tap the badge to see the list.
  const [recentErrors, setRecentErrors] = useState<LogEntry[]>([]);

  // Scan-QR flow (existing device sending sealed key)
  const videoRef = useRef<HTMLVideoElement>(null);
  const scanRafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [scanMessage, setScanMessage] = useState<string>('');
  const [scanSuccess, setScanSuccess] = useState(false);

  useEffect(() => {
    setEnrolled(syncClient.getRoom() != null);
    return syncClient.onStatusChange(s => setStatus(s as SyncStatus));
  }, []);

  // Poll the debug log for error-level entries. Every 4 seconds is
  // slow enough to be cheap (the log is in IDB) and fast enough that
  // a newly-appeared error lands in the badge within a breath.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const errs = await readRecentErrors(20);
        if (!cancelled) setRecentErrors(errs);
      } catch {
        // non-fatal: the badge just won't update this tick
      }
    };
    void tick();
    const handle = window.setInterval(() => { void tick(); }, 4000);
    return () => { cancelled = true; window.clearInterval(handle); };
  }, []);

  // Clean up media + sockets on unmount / mode change
  useEffect(() => {
    return () => {
      stopCamera();
      closePairSocket();
    };
  }, []);

  function closePairSocket() {
    if (pairWsRef.current) {
      try { pairWsRef.current.close(); } catch { /* ignore */ }
      pairWsRef.current = null;
    }
    pairSessionRef.current = null;
  }

  function stopCamera() {
    if (scanRafRef.current != null) {
      cancelAnimationFrame(scanRafRef.current);
      scanRafRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
  }

  function cancelMode() {
    console.log('[ReactDiag] cancelMode() called — returning to idle');
    pushReactDiag('handler-called', { handler: 'cancelMode' });
    stopCamera();
    closePairSocket();
    setMode('idle');
    setError(null);
    setPairMessage('');
    setScanMessage('');
    setPairSuccess(false);
    setScanSuccess(false);
    setQrDataUrl('');
    setNewPhrase('');
    setRestoreInput('');
    setRevealedPhrase(null);
  }

  // ---------- Set up new (first device) ----------

  function startNewSetup() {
    console.log('[ReactDiag] startNewSetup() called — "Set up sync" button clicked');
    pushReactDiag('handler-called', { handler: 'startNewSetup' });
    setError(null);
    const phrase = syncClient.generatePhrase();
    setNewPhrase(phrase);
    setMode('show-new-phrase');
  }

  function confirmNewPhrase() {
    try {
      syncClient.enrollPhrase(newPhrase);
      setEnrolled(true);
      setMode('idle');
      setNewPhrase('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'enroll failed');
    }
  }

  // ---------- Restore from phrase ----------

  function startRestore() {
    console.log('[ReactDiag] startRestore() called — user clicked "Restore from recovery phrase"');
    pushReactDiag('handler-called', { handler: 'startRestore' });
    setError(null);
    setRestoreInput('');
    setMode('restore-phrase');
  }

  function confirmRestore() {
    console.log('[ReactDiag] confirmRestore() called — final Restore button clicked');
    pushReactDiag('handler-called', { handler: 'confirmRestore' });
    try {
      syncClient.enrollPhrase(restoreInput);
      setEnrolled(true);
      // Joining an existing account via phrase = this device synced. Count once.
      void reportSyncedOnce();
      setMode('idle');
      setRestoreInput('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'invalid recovery phrase');
    }
  }

  // ---------- Show QR (new device, no phrase handy) ----------

  async function startShowQR() {
    console.log('[ReactDiag] startShowQR() called — "Pair using QR" button clicked');
    pushReactDiag('handler-called', { handler: 'startShowQR' });
    setError(null);
    setPairMessage(t.syncShowQrWaiting);
    const session = createPairSession();
    pairSessionRef.current = session;
    const payload = encodePairQR(session);
    log.info('sync', 'pair-qr-show', { roomId: session.roomId });
    try {
      const dataUrl = await QRCode.toDataURL(payload, { width: 280, margin: 1 });
      setQrDataUrl(dataUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'QR generation failed');
      return;
    }
    setMode('show-qr');

    // Open a WS to the pair room and listen for a `pair` envelope.
    const ws = new WebSocket(`${RELAY_URL}?room=${encodeURIComponent(session.roomId)}`);
    pairWsRef.current = ws;
    ws.addEventListener('open', () => {
      setPairMessage(t.syncShowQrWaiting);
      // We don't need to send anything — just listen.
    });
    ws.addEventListener('message', (ev) => {
      if (typeof ev.data !== 'string') return;
      try {
        const msg = JSON.parse(ev.data) as { type?: string; bytes?: string };
        if (msg.type !== 'pair' || !msg.bytes) return;
        const sealed = base64ToBytes(msg.bytes);
        const s = pairSessionRef.current;
        if (!s) return;
        const key = openSealedAccountKey(sealed, s.ephemeralPublicKey, s.ephemeralSecretKey);
        log.info('sync', 'pair-qr-receive-ok', { roomId: s.roomId });
        closePairSocket();
        syncClient.enrollReceivedKey(key);
        setEnrolled(true);
        // Received the account key via QR = this device joined sync. Count once.
        void reportSyncedOnce();
        setPairMessage(t.syncShowQrPaired);
        setPairSuccess(true);
        setTimeout(() => {
          setMode('idle');
          setPairMessage('');
          setPairSuccess(false);
        }, 2800);
      } catch (e) {
        log.error('sync', 'pair-qr-receive-failed', { error: String(e) });
        setError(e instanceof Error ? e.message : 'pair receive failed');
      }
    });
    ws.addEventListener('error', () => setError('pair relay connection failed'));
  }

  // ---------- Scan QR (existing device, sending key) ----------

  async function startScanQR() {
    setError(null);
    setScanMessage(t.syncScanOpening);
    setMode('scan-qr');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) { throw new Error('video element missing'); }
      video.srcObject = stream;
      await video.play();
      setScanMessage(t.syncScanInstructions);
      tickScan();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'camera access failed');
      stopCamera();
    }
  }

  function tickScan() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      scanRafRef.current = requestAnimationFrame(tickScan);
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const result = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
    if (result?.data) {
      handleScannedQR(result.data);
      return;
    }
    scanRafRef.current = requestAnimationFrame(tickScan);
  }

  function handleScannedQR(payload: string) {
    try {
      const { roomId, ephemeralPublicKey } = decodePairQR(payload);
      log.info('sync', 'pair-qr-scan-ok', { roomId });
      stopCamera();
      setScanMessage(t.syncScanSending);

      // Load our local account key (we're the existing device).
      const key = loadKey();
      if (!key) throw new Error('no account key on this device');

      const sealed = sealAccountKey(key, ephemeralPublicKey);
      const b64 = bytesToBase64(sealed);
      const ws = new WebSocket(`${RELAY_URL}?room=${encodeURIComponent(roomId)}`);
      ws.addEventListener('open', () => {
        try {
          ws.send(JSON.stringify({ type: 'pair', bytes: b64 }));
          log.info('sync', 'pair-qr-send-ok', { roomId });
          setScanMessage(t.syncScanSent);
          setScanSuccess(true);
          setTimeout(() => {
            try { ws.close(); } catch { /* ignore */ }
            setMode('idle');
            setScanMessage('');
            setScanSuccess(false);
          }, 2800);
        } catch (e) {
          setError(e instanceof Error ? e.message : 'send failed');
        }
      });
      ws.addEventListener('error', () => setError('pair relay connection failed'));
    } catch (e) {
      log.error('sync', 'pair-qr-scan-failed', { error: String(e) });
      setError(e instanceof Error ? e.message : 'invalid QR');
      // Keep scanning after a failed parse
      scanRafRef.current = requestAnimationFrame(tickScan);
    }
  }

  // ---------- Show existing phrase ----------

  function revealPhrase() {
    const phrase = syncClient.getPhrase();
    setRevealedPhrase(phrase);
    setMode('show-phrase');
  }

  // Track the most-recent successful copy so the button can flash a
  // "Copied!" confirmation. Veronika reported the previous silent
  // behaviour made it impossible to tell whether the click registered.
  // Two independent flags: the phrase copy and the error-log copy live
  // in different modes and must not share state (build 1411 bug: the
  // show-errors button read `phraseCopied`, which copyPhrase never sets
  // when there's no phrase, so it never flashed "Copied").
  const [phraseCopied, setPhraseCopied] = useState(false);
  const [errorsCopied, setErrorsCopied] = useState(false);
  async function copyPhrase() {
    const p = revealedPhrase ?? newPhrase;
    if (!p) return;
    try {
      await navigator.clipboard.writeText(p);
      setPhraseCopied(true);
      setTimeout(() => setPhraseCopied(false), 1800);
    } catch { /* ignore */ }
  }
  async function copyErrors() {
    const text = recentErrors
      .map(e => `[${new Date(e.ts).toISOString()}] ${e.area}:${e.event} ${e.data !== undefined ? JSON.stringify(e.data) : ''}`)
      .join('\n');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setErrorsCopied(true);
      setTimeout(() => setErrorsCopied(false), 1800);
    } catch { /* ignore */ }
  }

  function handleDisconnect() {
    if (!confirm(t.syncDisconnectConfirm)) return;
    syncClient.reset();
    setEnrolled(false);
    setMode('idle');
  }

  // ---------- Render ----------

  const statusLabel =
    status === 'connected' ? t.syncConnected
    : status === 'connecting' ? t.syncConnecting
    : status === 'error' ? t.syncError
    : t.syncDisconnected;

  return (
    <div className="space-y-3">
      {(enrolled || (recentErrors.length > 0 && mode === 'idle')) && (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}
          data-sync-status={status}
        >
          {recentErrors.length > 0 && mode === 'idle' && (
            <Chip
              onClick={() => setMode('show-errors')}
              title={t.syncErrorsBadge(recentErrors.length)}
              tone="warning"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Info size={10} aria-hidden /> {recentErrors.length}
            </Chip>
          )}
          {enrolled && (
            /* Colour-coded, because "is it working?" should be answerable at a
               glance rather than by reading (Veronika, 2026-08-23: "Shouldn't
               the 'connected' be green when everything works and red when its
               not"). Uses the Chip primitive's existing tones — no new colours
               — so it matches every other status pill in the app.
               'disconnected' counts as danger here: this chip only renders when
               the device IS enrolled, so not being connected is a fault, not a
               resting state. */
            <Chip
              tone={
                status === 'connected' ? 'success'
                : status === 'connecting' ? 'warning'
                : 'danger'
              }
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {status === 'connected' ? <Wifi size={10} aria-hidden /> : <WifiOff size={10} aria-hidden />}
              {statusLabel}
            </Chip>
          )}
        </div>
      )}

      {/* === Stable Choice Header for non-enrolled users (Option A) ===
           2026-05-19 build 1480 (Veronika msg 4228): this onboarding
           block was the last surviving pre-zine surface in Settings.
           Veronika saw it on a fresh iPad reinstall and called it
           "old visual". Ported to ZineButton (solid ink for primary,
           outlined for the two pair-existing options) so it matches
           the enrolled-state block right below and the rest of /set. */}
      {!enrolled && (
        <div className="mb-4" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ margin: 0, fontFamily: 'var(--font-geist-sans), system-ui, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
            How do you want to set up sync?
          </p>
          <ZineButton
            variant="solid"
            size="small"
            fullWidth
            onClick={startNewSetup}
            data-testid="sync-setup-fresh-button"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <KeyRound size={12} aria-hidden />
            {t.syncBtnSetup}
          </ZineButton>
          <ZineButton
            variant="outlined"
            size="small"
            fullWidth
            onClick={startRestore}
            data-testid="sync-pair-phrase-button"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {t.syncBtnRestore}
          </ZineButton>
          <ZineButton
            variant="outlined"
            size="small"
            fullWidth
            onClick={startShowQR}
            data-testid="sync-pair-qr-button"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <QrCode size={12} aria-hidden />
            {t.syncBtnShowQr}
          </ZineButton>
        </div>
      )}

      {mode === 'idle' && enrolled && (
        <>
          <p className="text-xs text-text-tertiary">{t.syncEnrolledHint}</p>
          {/* 2026-05-13 build 1402 — Veronika: the QR-scan camera
              path is flaky on iOS PWA, and she actively syncs new
              devices via the 12-word phrase. So "Show my recovery
              phrase" is now the primary CTA (ink-fill solid) and the
              QR scanner is demoted to an outlined secondary. The
              enrolled-hint copy was rewritten to describe the
              phrase path as the way to add a device. */}
          <ZineButton
            variant="solid"
            size="small"
            onClick={revealPhrase}
            data-testid="sync-show-phrase-button"
            style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <KeyRound size={12} aria-hidden />
            {t.syncBtnShowPhrase}
          </ZineButton>
          <ZineButton
            variant="outlined"
            size="small"
            onClick={startScanQR}
            data-testid="sync-scan-qr-button"
            style={{ width: '100%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <ScanLine size={12} aria-hidden />
            {t.syncBtnScanQr}
          </ZineButton>
          <ZineButton
            variant="outlined"
            size="small"
            onClick={handleDisconnect}
            data-testid="sync-disconnect-button"
            style={{
              width: '100%',
              flex: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              color: 'var(--ink-2)',
              borderColor: 'var(--ink-2)',
            }}
          >
            {t.syncBtnDisconnect}
          </ZineButton>
        </>
      )}

      {mode === 'show-new-phrase' && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
            <ShieldAlert size={16} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-900 leading-relaxed space-y-2">
              <p className="font-semibold">{t.syncNewPhraseTitle}</p>
              <p>{t.syncNewPhraseWhat}</p>
              <p>{t.syncNewPhraseTodoIntro}</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>{t.syncNewPhraseStep1}</li>
                <li>{t.syncNewPhraseStep2Pre}<b>{t.syncNewPhraseStep2Bold}</b>{t.syncNewPhraseStep2Post}</li>
                <li>{t.syncNewPhraseStep3}</li>
              </ol>
              <p className="italic">{t.syncNewPhraseWhyPaper}</p>
            </div>
          </div>
          <PhraseGrid phrase={newPhrase} />
          <div className="flex gap-2">
            <CopyButton copied={phraseCopied} onCopy={copyPhrase} />
            <button
              onClick={cancelMode}
              data-testid="sync-cancel-new-phrase-button"
              className="flex-1 py-2 rounded-xl border border-border-light text-xs font-medium text-text-tertiary hover:bg-bg-elevated transition-colors"
            >
              {t.syncCancel}
            </button>
          </div>
          <button
            onClick={confirmNewPhrase}
            data-testid="sync-confirm-new-phrase-button"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors"
          >
            <Check size={15} /> {t.syncNewPhraseConfirmBtn}
          </button>
        </div>
      )}

      {mode === 'show-phrase' && revealedPhrase && (
        <div className="space-y-3">
          <p className="text-[11px] text-text-tertiary">{t.syncShowPhraseHint}</p>
          <PhraseGrid phrase={revealedPhrase} />
          <div className="flex gap-2">
            <CopyButton copied={phraseCopied} onCopy={copyPhrase} />
            <button onClick={cancelMode} data-testid="sync-done-show-phrase-button" className="flex-1 py-2 rounded-xl border border-border-light text-xs font-medium text-text-tertiary hover:bg-bg-elevated transition-colors">
              {t.syncDone}
            </button>
          </div>
        </div>
      )}

      {mode === 'restore-phrase' && (
        <div className="space-y-3">
          <p className="text-[11px] text-text-tertiary">{t.syncRestoreHint}</p>
          <textarea
            value={restoreInput}
            onChange={e => setRestoreInput(e.target.value)}
            placeholder={t.syncRestorePlaceholder}
            rows={3}
            autoComplete="off"
            className="w-full px-3 py-2 bg-bg-elevated border border-border-light rounded-xl text-xs font-mono text-text-primary placeholder:text-text-tertiary resize-none"
          />
          {error && <p className="text-[10px] text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={cancelMode} data-testid="sync-cancel-restore-button" className="flex-1 py-2 rounded-xl border border-border-light text-xs font-medium text-text-tertiary hover:bg-bg-elevated transition-colors">
              {t.syncCancel}
            </button>
            <button
              onClick={confirmRestore}
              disabled={!restoreInput.trim()}
              data-testid="sync-confirm-restore-button"
              className="flex-1 py-2 rounded-xl bg-accent text-white text-xs font-medium disabled:opacity-40 hover:bg-accent/90 transition-colors"
            >
              {t.syncRestoreBtn}
            </button>
          </div>
        </div>
      )}

      {mode === 'show-qr' && (
        <div className="space-y-3">
          <p className="text-[11px] text-text-tertiary text-center">
            {t.syncShowQrHintPre}<b>{t.syncShowQrHintBold}</b>{t.syncShowQrHintPost}
          </p>
          {qrDataUrl && !pairSuccess && (
            <div className="flex justify-center p-3 rounded-xl bg-white">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={qrDataUrl} alt="pairing QR code" width={280} height={280} />
            </div>
          )}
          {pairSuccess ? (
            <div className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl bg-emerald-50 border border-emerald-200">
              <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center">
                <Check size={30} strokeWidth={3} className="text-white" />
              </div>
              <p className="text-base font-semibold text-emerald-800">{pairMessage}</p>
            </div>
          ) : (
            <p className="text-[10px] text-text-tertiary text-center">{pairMessage}</p>
          )}
          <button onClick={cancelMode} data-testid="sync-cancel-qr-button" className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-border-light text-xs font-medium text-text-tertiary hover:bg-bg-elevated transition-colors">
            <X size={13} /> {t.syncCancel}
          </button>
        </div>
      )}

      {mode === 'scan-qr' && (
        <div className="space-y-3">
          {!scanSuccess && (
            <div className="rounded-xl overflow-hidden bg-black aspect-square">
              <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            </div>
          )}
          {scanSuccess ? (
            <div className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl bg-emerald-50 border border-emerald-200">
              <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center">
                <Check size={30} strokeWidth={3} className="text-white" />
              </div>
              <p className="text-base font-semibold text-emerald-800">{scanMessage}</p>
            </div>
          ) : (
            <p className="text-[10px] text-text-tertiary text-center">{scanMessage}</p>
          )}
          {!scanSuccess && (
            <button onClick={cancelMode} data-testid="sync-cancel-scan-button" className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-border-light text-xs font-medium text-text-tertiary hover:bg-bg-elevated transition-colors">
              <X size={13} /> {t.syncCancel}
            </button>
          )}
        </div>
      )}

      {mode === 'show-errors' && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
            <AlertTriangle size={16} className="text-red-700 shrink-0 mt-0.5" />
            <div className="text-[11px] text-red-900 leading-relaxed space-y-1">
              <p className="font-semibold">{t.syncErrorsTitle}</p>
              <p>{t.syncErrorsHint}</p>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1.5">
            {recentErrors.map(e => (
              <div key={e.seq} className="p-2 rounded-lg bg-bg-elevated border border-border-light">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[10px] font-mono font-semibold text-red-700">
                    {e.area}:{e.event}
                  </span>
                  <span className="text-[9px] font-mono text-text-tertiary shrink-0">
                    {new Date(e.ts).toLocaleTimeString()}
                  </span>
                </div>
                {e.data !== undefined && (
                  <pre className="mt-1 text-[9px] font-mono text-text-secondary whitespace-pre-wrap break-all">
                    {typeof e.data === 'string' ? e.data : JSON.stringify(e.data)}
                  </pre>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <CopyButton copied={errorsCopied} onCopy={copyErrors} />
            <button
              onClick={cancelMode}
              data-testid="sync-done-errors-button"
              className="flex-1 py-2 rounded-xl border border-border-light text-xs font-medium text-text-tertiary hover:bg-bg-elevated transition-colors"
            >
              {t.syncDone}
            </button>
          </div>
        </div>
      )}

      {error && mode !== 'restore-phrase' && (
        <p className="text-[10px] text-red-600">{error}</p>
      )}
    </div>
  );
}

// Shared "Copy / Copied" button used by the show-new-phrase,
// show-phrase, and show-errors flows. Flashes a green confirmation
// while `copied` is true. Extracted in the 1394–1411 review sweep —
// the three call sites had drifted (the errors one read the wrong
// state flag), and a single component makes that class of bug
// structurally impossible.
function CopyButton({ copied, onCopy }: { copied: boolean; onCopy: () => void }) {
  const { t } = useI18n();
  return (
    <button
      onClick={onCopy}
      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-xs font-medium transition-colors ${
        copied
          ? 'bg-green-50 border-green-200 text-green-700'
          : 'bg-bg-elevated border-border-light text-text-secondary hover:bg-bg-card'
      }`}
    >
      {copied ? (
        <><Check size={13} /> {t.syncCopied}</>
      ) : (
        <><Copy size={13} /> {t.syncCopy}</>
      )}
    </button>
  );
}

// PhraseGrid moved to src/components/PhraseGrid.tsx (build 1502) so the
// Settings "Data safety" recovery-phrase modal can share the rendering.
